import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { MinisiteRichEditor } from '../../components/admin/MinisiteRichEditor';
import { Button } from '../../components/ui/button';
import { 
  ArrowLeft, 
  Save, 
  Eye,
  EyeOff,
  Image as ImageIcon,
  Type,
  MousePointer2,
  GripVertical,
  Trash2,
  Plus,
  ExternalLink,
  Link as LinkIcon,
  Loader2,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Legacy in-file editor replaced by MinisiteRichEditor (TipTap-based).
// Kept as an alias to minimise diff in the rest of the file.
const RichTextEditor = MinisiteRichEditor;

// Sortable Box Component
const SortableBox = ({ box, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: box.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const [isEditing, setIsEditing] = useState(false);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white border border-[#E5E5E5] rounded-lg overflow-hidden ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* Box Header */}
      <div className="bg-[#F9F9F7] border-b border-[#E5E5E5] px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-white rounded"
          >
            <GripVertical className="w-4 h-4 text-[#999999]" />
          </button>
          <span className="text-xs font-medium text-[#666666] uppercase">
            {box.type === 'image' && 'Imatge'}
            {box.type === 'text' && 'Text'}
            {box.type === 'button' && 'Botó'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-1 hover:bg-white rounded text-[#666666]"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(box.id)}
            className="p-1 hover:bg-red-50 hover:text-red-600 rounded text-[#666666]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Box Content */}
      <div className="p-4">
        {box.type === 'image' && (
          <div className="space-y-3">
            {box.content ? (
              <img src={box.content} alt="" className="w-full rounded" />
            ) : (
              <div className="bg-[#F5F5F5] rounded-lg p-8 text-center">
                <ImageIcon className="w-8 h-8 text-[#CCCCCC] mx-auto mb-2" />
                <p className="text-sm text-[#999999]">Cap imatge</p>
              </div>
            )}
            {isEditing && (
              <div className="space-y-2 pt-2 border-t border-[#E5E5E5]">
                <label className="block text-xs font-medium text-[#666666]">URL de la imatge</label>
                <input
                  type="text"
                  value={box.content || ''}
                  onChange={(e) => onUpdate(box.id, { content: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                />
                <label className="block text-xs font-medium text-[#666666] mt-2">Enllaç (opcional)</label>
                <input
                  type="text"
                  value={box.link_url || ''}
                  onChange={(e) => onUpdate(box.id, { link_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                />
                <label className="block text-xs font-medium text-[#666666] mt-2">Text alt / rollover</label>
                <input
                  type="text"
                  value={box.link_text || ''}
                  onChange={(e) => onUpdate(box.id, { link_text: e.target.value })}
                  placeholder="Descripció de la imatge"
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                />
              </div>
            )}
          </div>
        )}

        {box.type === 'text' && (
          <div>
            {isEditing ? (
              <RichTextEditor
                value={box.content || ''}
                onChange={(html) => onUpdate(box.id, { content: html })}
              />
            ) : (
              <div 
                className="prose prose-sm max-w-none text-[#666666]"
                dangerouslySetInnerHTML={{ __html: box.content || '<p class="text-[#999999] italic">Fes clic a configuració per editar</p>' }}
              />
            )}
          </div>
        )}

        {box.type === 'button' && (
          <div className="space-y-3">
            {/* Button Preview */}
            <div className="flex justify-center py-2">
              <a
                href={box.link_url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center justify-center px-6 py-3 font-semibold text-sm tracking-wide transition-colors ${
                  box.button_color === 'gold' 
                    ? 'bg-[#D4AF37] hover:bg-[#C4A030] text-white'
                    : box.button_color === 'white'
                    ? 'bg-white border-2 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white'
                    : 'bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white'
                }`}
              >
                {box.button_text || 'Text del botó'}
              </a>
            </div>
            
            {isEditing && (
              <div className="space-y-3 pt-2 border-t border-[#E5E5E5]">
                <div>
                  <label className="block text-xs font-medium text-[#666666] mb-1">Text del botó</label>
                  <input
                    type="text"
                    value={box.button_text || ''}
                    onChange={(e) => onUpdate(box.id, { button_text: e.target.value })}
                    placeholder="Clica aquí"
                    className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#666666] mb-1">URL destí</label>
                  <input
                    type="text"
                    value={box.link_url || ''}
                    onChange={(e) => onUpdate(box.id, { link_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#666666] mb-2">Color</label>
                  <div className="flex gap-2">
                    {['green', 'gold', 'white'].map((color) => (
                      <button
                        key={color}
                        onClick={() => onUpdate(box.id, { button_color: color })}
                        className={`flex-1 py-2 px-3 rounded text-xs font-medium transition-all ${
                          box.button_color === color ? 'ring-2 ring-offset-2 ring-[#1A4D2E]' : ''
                        } ${
                          color === 'gold' 
                            ? 'bg-[#D4AF37] text-white'
                            : color === 'white'
                            ? 'bg-white border-2 border-[#1A4D2E] text-[#1A4D2E]'
                            : 'bg-[#1A4D2E] text-white'
                        }`}
                      >
                        {color === 'green' ? 'Verd' : color === 'gold' ? 'Daurat' : 'Blanc'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Column Component
const Column = ({ title, boxes, onAddBox, onUpdateBox, onDeleteBox, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = boxes.findIndex(b => b.id === active.id);
      const newIndex = boxes.findIndex(b => b.id === over.id);
      onReorder(arrayMove(boxes, oldIndex, newIndex));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#666666] uppercase tracking-wider">
          {title}
        </h3>
        <div className="flex gap-1">
          <button
            onClick={() => onAddBox('image')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded text-[#666666]"
            title="Afegir imatge"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAddBox('text')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded text-[#666666]"
            title="Afegir text"
          >
            <Type className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAddBox('button')}
            className="p-1.5 hover:bg-[#F5F5F5] rounded text-[#666666]"
            title="Afegir botó"
          >
            <MousePointer2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={boxes.map(b => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3 min-h-[100px]">
            {boxes.length === 0 ? (
              <div className="border-2 border-dashed border-[#E5E5E5] rounded-lg p-8 text-center">
                <Plus className="w-6 h-6 text-[#CCCCCC] mx-auto mb-2" />
                <p className="text-sm text-[#999999]">
                  Afegeix una caixa
                </p>
              </div>
            ) : (
              boxes.map((box) => (
                <SortableBox
                  key={box.id}
                  box={box}
                  onUpdate={onUpdateBox}
                  onDelete={onDeleteBox}
                />
              ))
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

const AdminMinisiteEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minisite, setMinisite] = useState({
    title: '',
    slug: '',
    custom_url: '',
    header_image: '',
    seo_title: '',
    seo_description: '',
    status: 'draft',
    left_column: [],
    right_column: []
  });
  const [showSeoSettings, setShowSeoSettings] = useState(false);

  useEffect(() => {
    if (id) {
      fetchMinisite();
    }
  }, [id]);

  const fetchMinisite = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/${id}`);
      if (response.ok) {
        const data = await response.json();
        setMinisite(data);
      }
    } catch (error) {
      console.error('Error fetching minisite:', error);
      toast.error('Error carregant minisite');
    } finally {
      setLoading(false);
    }
  };

  const saveMinisite = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/minisites/admin/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(minisite)
      });
      
      if (response.ok) {
        toast.success('Minisite guardat');
      } else {
        let detail = `HTTP ${response.status}`;
        try {
          const errData = await response.json();
          if (errData?.detail) detail = errData.detail;
        } catch (_) {}
        throw new Error(detail);
      }
    } catch (error) {
      console.error('Error saving minisite:', error);
      toast.error(`Error guardant minisite: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const addBox = (column, type) => {
    const newBox = {
      id: `box_${Date.now()}`,
      type,
      content: '',
      link_url: '',
      link_text: '',
      button_color: 'green',
      button_text: '',
      order: column === 'left' ? minisite.left_column.length : minisite.right_column.length
    };

    if (column === 'left') {
      setMinisite(prev => ({
        ...prev,
        left_column: [...prev.left_column, newBox]
      }));
    } else {
      setMinisite(prev => ({
        ...prev,
        right_column: [...prev.right_column, newBox]
      }));
    }
  };

  const updateBox = (column, boxId, updates) => {
    const columnKey = column === 'left' ? 'left_column' : 'right_column';
    setMinisite(prev => ({
      ...prev,
      [columnKey]: prev[columnKey].map(box =>
        box.id === boxId ? { ...box, ...updates } : box
      )
    }));
  };

  const deleteBox = (column, boxId) => {
    const columnKey = column === 'left' ? 'left_column' : 'right_column';
    setMinisite(prev => ({
      ...prev,
      [columnKey]: prev[columnKey].filter(box => box.id !== boxId)
    }));
  };

  const reorderBoxes = (column, newBoxes) => {
    const columnKey = column === 'left' ? 'left_column' : 'right_column';
    setMinisite(prev => ({
      ...prev,
      [columnKey]: newBoxes.map((box, index) => ({ ...box, order: index }))
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-minisite-editor">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/minisites')}
              className="p-2 hover:bg-[#F5F5F5] rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <input
                type="text"
                value={minisite.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setMinisite(prev => ({
                    ...prev,
                    title: newTitle,
                    slug: prev.slug || generateSlug(newTitle),
                    seo_title: prev.seo_title || newTitle
                  }));
                }}
                className="text-2xl font-display font-semibold text-[#1A4D2E] bg-transparent border-none focus:outline-none focus:ring-0 w-full"
                placeholder="Títol del minisite"
              />
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-[#888888]">/m/</span>
                <input
                  type="text"
                  value={minisite.slug}
                  onChange={(e) => setMinisite(prev => ({ ...prev, slug: e.target.value }))}
                  className="text-sm text-[#666666] bg-[#F5F5F5] px-2 py-0.5 rounded border-none focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 w-32"
                />
                <span className="text-sm text-[#888888] ml-2">o URL personalitzada:</span>
                <span className="text-sm text-[#888888]">/</span>
                <input
                  type="text"
                  value={minisite.custom_url || ''}
                  onChange={(e) => setMinisite(prev => ({ ...prev, custom_url: e.target.value }))}
                  placeholder="ex: anyos-park"
                  className="text-sm text-[#666666] bg-[#F5F5F5] px-2 py-0.5 rounded border-none focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20 w-32"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSeoSettings(!showSeoSettings)}
              className={`px-3 py-2 text-sm rounded-lg border ${showSeoSettings ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'border-[#E5E5E5] text-[#666666] hover:bg-[#F5F5F5]'}`}
            >
              SEO
            </button>
            <select
              value={minisite.status}
              onChange={(e) => setMinisite(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
            >
              <option value="draft">Esborrany</option>
              <option value="published">Publicat</option>
            </select>
            {minisite.status === 'published' && (
              <a
                href={minisite.custom_url ? `/${minisite.custom_url}` : `/m/${minisite.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-[#F5F5F5] rounded-lg text-[#666666]"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
            <Button
              onClick={saveMinisite}
              disabled={saving}
              className="bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </div>

        {/* SEO Settings */}
        {showSeoSettings && (
          <div className="bg-[#F9F9F7] rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-[#666666] uppercase tracking-wider">
              Configuració SEO
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-[#666666] mb-1">
                  Títol SEO
                </label>
                <input
                  type="text"
                  value={minisite.seo_title}
                  onChange={(e) => setMinisite(prev => ({ ...prev, seo_title: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  placeholder="Títol per motors de cerca"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#666666] mb-1">
                  Meta descripció
                </label>
                <input
                  type="text"
                  value={minisite.seo_description}
                  onChange={(e) => setMinisite(prev => ({ ...prev, seo_description: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
                  placeholder="Descripció breu per resultats de cerca"
                />
              </div>
            </div>
          </div>
        )}

        {/* Header Image */}
        <div className="bg-white rounded-lg border border-[#E5E5E5] p-4">
          <label className="block text-sm font-medium text-[#666666] mb-2">
            Imatge de capçalera
          </label>
          <div className="flex gap-4">
            <input
              type="text"
              value={minisite.header_image}
              onChange={(e) => setMinisite(prev => ({ ...prev, header_image: e.target.value }))}
              placeholder="URL de la imatge (des de Documents/Galeria)"
              className="flex-1 px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/20"
            />
          </div>
          {minisite.header_image && (
            <img 
              src={minisite.header_image} 
              alt="Capçalera" 
              className="mt-3 w-full h-48 object-cover rounded-lg"
            />
          )}
        </div>

        {/* Two Column Editor */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-[#E5E5E5] p-4">
            <Column
              title="Columna esquerra (2/3)"
              boxes={minisite.left_column}
              onAddBox={(type) => addBox('left', type)}
              onUpdateBox={(boxId, updates) => updateBox('left', boxId, updates)}
              onDeleteBox={(boxId) => deleteBox('left', boxId)}
              onReorder={(newBoxes) => reorderBoxes('left', newBoxes)}
            />
          </div>

          {/* Right Column - 1/3 */}
          <div className="lg:col-span-1 bg-white rounded-lg border border-[#E5E5E5] p-4">
            <Column
              title="Columna dreta (1/3)"
              boxes={minisite.right_column}
              onAddBox={(type) => addBox('right', type)}
              onUpdateBox={(boxId, updates) => updateBox('right', boxId, updates)}
              onDeleteBox={(boxId) => deleteBox('right', boxId)}
              onReorder={(newBoxes) => reorderBoxes('right', newBoxes)}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMinisiteEditor;
